import { PubSub } from "aws-amplify";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Accordion } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { backendConfig } from "../../../config/BackendConfig";
import { OrganisationContext, UserContext } from "../../../layouts/MainLayout";
import {
  getDeviceFirmwareUpdateHistoryDetails,
  getLatestFirmwareUpdateDetails,
  initiateOTAUpgrade,
} from "../../../services/OTAService";
import LoadingSpinner from "../../../utils/components/common/loadingSpinner/LoadingSpinner";
import Modal from "../../../utils/components/common/modal";
import ModalComponent from "../../../utils/components/common/modalComponent/ModalComponent";
import Icons from "../../../utils/components/icons";
import { USER_ROLES } from "../../../utils/constants/userConstants";
import {
  extractVersion,
  formatDate,
  millisToMinutesAndSeconds,
} from "../../../utils/utils";
import "./OTAUpdates.scss";

function OTAUpdates({
  deviceData,
  closeModal,
  systemUpToDate,
  otaStatus,
  refreshDevicePage,
}) {
  const { t } = useTranslation("ota");
  const { loggedInUserRole } = useContext(UserContext);
  const { siteData } = useContext(OrganisationContext);

  const updateTableHeaders = [
    "Version",
    "Type",
    "Updated On",
    "Updated By",
    "",
  ];
  const [isLoading, setIsLoading] = useState(false);
  const [otaInitiated, setOtaInitiated] = useState(false); // after initiating the OTA
  const [updateHistoryDetails, setUpdateHistoryDetails] = useState([]);
  const [currentUpdateHistoryDetails, setCurrentUpdateHistoryDetails] =
    useState(null);
  const [releaseNotesDetails, setReleaseNotesDetails] = useState([]);
  const [otaUpdatesMqttData, setOtaUpdatesMqttData] = useState();
  const [updatesHeading, setUpdatesHeading] = useState({
    text: "",
    version: extractVersion(deviceData?.controllerVersion),
    updateType: "",
  });
  const [isUpdateModalDesc, setIsUpdateModalDesc] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState({
    acc1: !systemUpToDate,
    acc2: systemUpToDate,
  });
  const [onClickedIndex, setOnClickedIndex] = useState(null);
  const otaStatusSub = useRef(null);

  const disableInstallBtn = useMemo(() => {
    return (
      loggedInUserRole === USER_ROLES.viewOnlyUserCorp ||
      loggedInUserRole === USER_ROLES.userCorp ||
      deviceData?.mode === "OFFLINE"
    );
  }, [deviceData, loggedInUserRole]);

  /**Information and confirm modal states starts: */
  const [confirmModal, setConfirmModal] = useState(false);
  const [infoModal, setInfoModal] = useState({
    open: false,
    msg: "",
    closeParentModal: false,
  });
  const onInfoModalClose = useCallback(() => {
    setInfoModal((prev) => {
      if (prev.closeParentModal) {
        closeModal();
      }
      return { ...prev, open: false, msg: "", closeParentModal: false };
    });
  }, [closeModal]);
  /**Information and confirm modal states ends: */

  /**Update accordion expand/collapsed states**/
  const handleAccordionSelect = useCallback(() => {
    setActiveAccordion((prev) => ({ acc1: !prev.acc1, acc2: !prev.acc2 }));
  }, []);

  /**Show confirmation modal to initiate OTA**/
  const installUpdatesClicked = useCallback(() => {
    setConfirmModal(true);
  }, []);

  /**Seq-2: API call to get OTA updates details and history table details & initiate MQTT subscription */
  const getOTADetails = useCallback(async () => {
    if (otaStatus) {
      setIsLoading(true);
      try {
        let updatesDetails;
        if (otaStatus === "UpdateAvailable" || otaStatus === "InProgress") {
          // fetch updates details
          const queryParam = {
            size: 1,
          };
          updatesDetails = await getLatestFirmwareUpdateDetails(queryParam);
          setReleaseNotesDetails(
            updatesDetails.data.items[0]?.releaseNote || []
          );
          setCurrentUpdateHistoryDetails(
            updatesDetails?.data?.items[0] || null
          );
        }
        const historyQueryParam = {
          deviceId: deviceData?.DeviceUUID,
          filters: {
            otaStatus: ["Success"],
          },
          // size: 3, // for institute user the size is 3. In future for other user the size will be n
        };
        const updatesHistoryData = await getDeviceFirmwareUpdateHistoryDetails(
          historyQueryParam
        );
        setUpdateHistoryDetails(updatesHistoryData?.data?.items || []);
        if (otaStatus === "UpdateAvailable") {
          setUpdatesHeading({
            text: t("updatesHeadings.newUpdateAvailable"),
            version: extractVersion(
              updatesDetails.data.items[0]?.releaseVersion
            ),
            updateType: updatesDetails?.data?.items[0]?.releaseType,
          });
        }
        if (otaStatus === "InProgress") {
          setUpdatesHeading({
            text: t("updatesHeadings.updateInProgress"),
            version: extractVersion(
              updatesDetails.data.items[0]?.releaseVersion
            ),
            updateType: updatesDetails?.data?.items[0]?.releaseType,
          });
        }
        if (otaStatus === "UpToDate") {
          setUpdatesHeading({
            text: t("updatesHeadings.upToDate"),
            version: updatesHistoryData?.data?.items.length
              ? extractVersion(
                  updatesHistoryData?.data?.items[0].releaseVersion
                )
              : extractVersion(deviceData?.controllerVersion),
            updateType: updatesHistoryData?.data?.items.length
              ? updatesHistoryData?.data?.items[0].releaseType
              : "",
          });
        }
        setIsLoading(false);
      } catch (e) {
        setInfoModal({
          open: true,
          msg: "somethingWentWrong",
          closeParentModal: true,
        });
      }
    }
  }, [deviceData, otaStatus, t]);

  /**Seq-1: To get OTA updates details and history table details */
  useEffect(() => {
    getOTADetails();
  }, [getOTADetails, otaStatus]);

  /**Seq-4: MQTT subscription*/
  const otaUpdateSubscription = useCallback(async () => {
    try {
      const historyQueryParam = {
        deviceId: deviceData?.DeviceUUID,
        filters: {
          otaStatus: [
            "UpdateInProgress",
            "DownloadStarted",
            "DownloadingUpdates",
            "DownloadingCompleted",
            "DeviceMovedToStandby",
            "InstallingUpdates",
            "InstallingCompleted",
            "DeviceRestarting",
            "CommunicationError",
            "Success",
            "Failed",
          ],
        },
        size: 1, //    for institute user the size is 3. In future for other user the size will be n
      };
      const response = await getDeviceFirmwareUpdateHistoryDetails(
        historyQueryParam
      );
      setOtaUpdatesMqttData(response?.data?.items[0]);
      console.info(
        `${response?.data?.items[0].otaStatus} --  ${response?.data?.items[0].percentageCompleted} --  ${response?.data?.items[0].estimatedTimeRemaining}`
      );
      if (otaStatusSub.current) {
        otaStatusSub.current.unsubscribe();
      }
      const url = `chorus/${backendConfig.PUBSUB_ENV}/sites/${siteData.id}/ota/notifications/events`;
      otaStatusSub.current = PubSub.subscribe(url).subscribe({
        next: (data) => {
          console.info(data?.value);
          setOtaUpdatesMqttData(data?.value);
        },
        error: (error) => {
          console.info(`OTA MQTT error : ${error}`);
        },
        close: () => console.log(""),
      });
    } catch (e) {}
  }, [deviceData, siteData?.id]);

  /**Seq-3: To initiate OTA API subscription**/
  useEffect(() => {
    if (otaStatus === "InProgress" || otaInitiated) {
      otaUpdateSubscription();
    }
  }, [otaStatus, otaInitiated, otaUpdateSubscription]);

  const updteHistoryOnsuccess = useCallback(async () => {
    try {
      const historyQueryParam = {
        deviceId: deviceData?.DeviceUUID,
        filters: {
          otaStatus: ["Success"],
        },
        // size: 3, // for institute user the size is 3. In future for other user the size will be n
      };
      const updatesHistoryData = await getDeviceFirmwareUpdateHistoryDetails(
        historyQueryParam
      );
      setUpdateHistoryDetails(updatesHistoryData?.data?.items || []);
    } catch (e) {}
  }, [deviceData?.DeviceUUID]);

  useEffect(() => {
    if (otaUpdatesMqttData?.otaStatus === "Success") {
      updteHistoryOnsuccess();
      setUpdatesHeading({
        text: t("updatesHeadings.upToDate"),
        version: extractVersion(
          updateHistoryDetails.length
            ? updateHistoryDetails[0]?.releaseVersion
            : ""
        ),
        updateType: updateHistoryDetails.length
          ? updateHistoryDetails[0]?.releaseType
          : "",
      });
    }
    if (otaUpdatesMqttData?.otaStatus === "Failed") {
      setUpdatesHeading({
        text: t("updatesHeadings.newUpdateAvailable"),
        version: extractVersion(
          currentUpdateHistoryDetails?.releaseVersion || ""
        ),
        updateType: currentUpdateHistoryDetails?.releaseType || "",
      });
    }
  }, [
    otaUpdatesMqttData,
    t,
    updateHistoryDetails,
    currentUpdateHistoryDetails,
    updteHistoryOnsuccess,
  ]);

  useEffect(() => {
    if (otaInitiated) {
      setUpdatesHeading({
        text: t("updatesHeadings.updateInProgress"),
        version: extractVersion(
          currentUpdateHistoryDetails?.releaseVersion || ""
        ),
        updateType: currentUpdateHistoryDetails?.releaseType || "",
      });
    }
  }, [
    currentUpdateHistoryDetails?.releaseType,
    currentUpdateHistoryDetails?.releaseVersion,
    otaInitiated,
    t,
  ]);

  /**Initiate OTA after closing the confirmation modal**/
  const initiateOTA = useCallback(async () => {
    try {
      setIsLoading(true);
      const queryParam = {
        // releaseVersion: "v1.0.9-26-af2994788",
        releaseVersion: currentUpdateHistoryDetails?.releaseVersion,
        deviceList: [deviceData?.DeviceUUID],
      };
      await initiateOTAUpgrade(queryParam);
      setOtaInitiated(true);
    } catch (e) {
      setInfoModal({
        open: true,
        msg: "notInitiated",
      });
    }
    setIsLoading(false);
  }, [deviceData, currentUpdateHistoryDetails]);

  const showProgressbar = useMemo(() => {
    if (
      otaUpdatesMqttData?.otaStatus === "CommunicationError" ||
      otaUpdatesMqttData?.otaStatus === "Success" ||
      otaUpdatesMqttData?.otaStatus === "Failed"
    ) {
      return false;
    }
    return true;
  }, [otaUpdatesMqttData]);

  const getReleaseList = useCallback((list) => {
    // logic for 512 chars
  }, []);

  const getProgressVal = (val = 3) => {
    if (val) {
      if (val) {
        if (val > 90) {
          return val - 5;
        }
        return val;
      } else {
        return 1;
      }
    }
  };

  const getEstimatedTimeRemaining = useCallback(() => {
    const val = millisToMinutesAndSeconds(
      otaUpdatesMqttData?.estimatedTimeRemaining
    );
    if (val >= 0) {
      if (val === 1) {
        return t("estimatedMin", { val: val });
      } else {
        return t("estimatedMins", { val: val });
      }
    } else {
      return t("estimatedMins", { val: "--" });
    }
  }, [otaUpdatesMqttData?.estimatedTimeRemaining, t]);

  return (
    <div>
      <Modal
        isOpen={true}
        onCloseModal={closeModal}
        idName="OTA-Modal"
        className={`${isUpdateModalDesc || confirmModal ? "hide-modal" : ""}`}
      >
        {isLoading && <LoadingSpinner />}
        {!isLoading && (
          <div>
            {/* Modal Header Starts */}
            <div className="header-section">
              <span className="heading-h1">
                System Updates - {deviceData?.DeviceName}
              </span>
            </div>
            {/* Modal Header Ends */}
            {/* Accordion Section Starts */}
            {otaStatus && (
              <div
                className={`accordion-section ${
                  systemUpToDate && "hide-toggle"
                }`}
              >
                <Accordion
                  activeKey={activeAccordion.acc1 ? "accordion-1" : ""}
                  onSelect={!systemUpToDate && handleAccordionSelect}
                  className={`accordion-1 ${
                    activeAccordion.acc1 ? "open" : ""
                  } ${
                    activeAccordion.acc1 &&
                    otaStatus === "UpdateAvailable" &&
                    !otaInitiated
                      ? "install-active"
                      : ""
                  }`}
                >
                  <Accordion.Item eventKey="accordion-1">
                    <Accordion.Header>
                      <span
                        className={`heading-h4 ota-type ${
                          updatesHeading.text ===
                          "Device System is up to date - "
                            ? "black"
                            : "version-color"
                        }`}
                      >
                        {updatesHeading.text}&nbsp;
                      </span>
                      <span className="heading-h4 version-color ota-version">
                        {updatesHeading.version}&nbsp;
                      </span>
                      <span
                        className={`heading-h4 update-type ${
                          updatesHeading.text ===
                          "Device System is up to date - "
                            ? "gray"
                            : "black"
                        }`}
                      >
                        {" "}
                        {updatesHeading?.updateType
                          ? `(${updatesHeading.updateType})`
                          : ""}
                      </span>
                    </Accordion.Header>{" "}
                    <Accordion.Body>
                      {/* Release notes heading */}
                      <div className="current-release-details">
                        <div className="release-heading black text-h1">
                          {releaseNotesDetails.length && releaseNotesDetails[0]}
                        </div>
                        <div className="release-desc">
                          {getReleaseList(releaseNotesDetails)}
                          <ul>
                            {releaseNotesDetails.length ? (
                              releaseNotesDetails.map((note, i) => {
                                return i ? (
                                  <>
                                    <li className="text-h1 gray release-list">
                                      {note}
                                    </li>
                                  </>
                                ) : (
                                  <></>
                                );
                              })
                            ) : (
                              <></>
                            )}
                          </ul>
                        </div>
                        <div className="black text-h1 release-link">
                          {t("chorusDetails")}{" "}
                          <a
                            href="https://www.meetchorus.com"
                            target="_blank"
                            className="version-color text-h1 anchor"
                            rel="noreferrer"
                          >
                            {t("releaseDetailsUrl")}
                          </a>
                        </div>
                      </div>
                      {otaStatus === "UpdateAvailable" && !otaInitiated && (
                        <div
                          className={`install-btn ${
                            disableInstallBtn ? "disable-install" : ""
                          }`}
                          disabled={disableInstallBtn}
                          onClick={!disableInstallBtn && installUpdatesClicked}
                        >
                          <span className="install-icon">{Icons.installOTA}</span>
                          <span className="text-h3 install-text">
                            {" "}
                            {t("installUpdatesBtn")}
                          </span>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>

                {/* Non-collapsible Progress div */}
                {otaStatus === "InProgress" || otaInitiated ? (
                  <>
                    <>
                      <div
                        className={
                          activeAccordion.acc1
                            ? "progressConatiner active"
                            : "progressConatiner"
                        }
                      >
                        {showProgressbar ? (
                          <div className="progress-bar-container">
                            {/* Progress bar section starts */}
                            <div className="statusContainer">
                              <span className="text-h3">
                                {otaUpdatesMqttData?.otaStatus
                                  ? t(`status.${otaUpdatesMqttData?.otaStatus}`)
                                  : t("status.DownloadingUpdates")}
                              </span>

                              {otaUpdatesMqttData?.estimatedTimeRemaining ? (
                                <span className="text-2 gray">
                                  <span>{getEstimatedTimeRemaining()}</span>
                                </span>
                              ) : (
                                <></>
                              )}
                            </div>

                            <div class="progress">
                              <div
                                class="progress-bar"
                                style={{
                                  width: `${Math.round(
                                    otaUpdatesMqttData?.percentageCompleted || 0
                                  )}%`,
                                }}
                              ></div>
                              <span class="progress-value">
                                {Math.round(
                                  otaUpdatesMqttData?.percentageCompleted || 0
                                )}
                                {document.documentElement.style.setProperty(
                                  "--customLeft",
                                  `${Math.round(
                                    getProgressVal(
                                      otaUpdatesMqttData?.percentageCompleted ||
                                        0
                                    )
                                  )}%`
                                )}
                                %
                              </span>
                            </div>
                            <p></p>
                            {/* Progress bar section ends */}
                          </div>
                        ) : (
                          <div className="updates-msgs">
                            {/* Communication section starts */}
                            {otaUpdatesMqttData?.otaStatus ===
                              "CommunicationError" && (
                              <div className="comm-err msgs">
                                {Icons.commErr}
                                <div>
                                  <div className="heading-h3 yellow heading">
                                    {t("comErr.heading")}
                                  </div>
                                  <div className="text-h2 gray">
                                    {t("comErr.desc1")}
                                  </div>
                                  <div className="text-h2 gray">
                                    {t("comErr.desc2")}
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* Communication section ends */}
                            {/* Success section starts */}
                            {otaUpdatesMqttData?.otaStatus === "Success" && (
                              <div className="ota-success msgs">
                                {Icons.otaSuccess}
                                <div>
                                  <div className="heading-h3 green heading success-heading">
                                    {t("success.heading")}
                                  </div>
                                  <div className="text-h2 gray success-desc">
                                    {t("success.desc")}
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* Success section ends */}
                            {/* Failed section starts */}
                            {otaUpdatesMqttData?.otaStatus === "Failed" && (
                              <>
                                <div className="ota-failed">
                                  <div className="msgs">
                                    {Icons.otaFail}
                                    <div>
                                      <div className="heading-h3 red heading">
                                        {t("failed.heading")}
                                      </div>
                                      <div className="text-h2">
                                        {t("failed.desc1")}
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <button
                                      className={`roundBtn primary try-again text-h3 ${
                                        disableInstallBtn
                                          ? "disable-install"
                                          : ""
                                      }`}
                                      disabled={disableInstallBtn}
                                      onClick={
                                        !disableInstallBtn && initiateOTA
                                      }
                                    >
                                      {t("tryAgain")}
                                    </button>
                                  </div>
                                </div>
                                <div className="text-h2 gray err-desc-2">
                                  {t("failed.desc2")}
                                </div>
                              </>
                            )}
                            {/* Failed section ends */}
                          </div>
                        )}
                      </div>
                    </>
                  </>
                ) : (
                  <></>
                )}

                <Accordion
                  activeKey={activeAccordion.acc2 ? "accordion-2" : ""}
                  onSelect={!systemUpToDate && handleAccordionSelect}
                  className={`accordion-2 ${
                    activeAccordion.acc1 &&
                    otaStatus === "UpdateAvailable" &&
                    !otaInitiated
                      ? "install-active"
                      : ""
                  }`}
                >
                  <Accordion.Item eventKey="accordion-2">
                    <Accordion.Header>
                      <span className="heading-h4 black">Updates History</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      <table
                        className="version-history-table table"
                        // className="table tableHeaderSticky"
                      >
                        <thead>
                          <tr key="ota">
                            {updateTableHeaders.map((header, index) => (
                              <td
                                key={header}
                                className={`${header} col-${index + 1} text-h2`}
                              >
                                <label id={`${header}-Label`}>
                                  {t(`${header}`)}
                                </label>
                              </td>
                            ))}
                          </tr>
                        </thead>

                        {updateHistoryDetails.length ? (
                          <tbody className="jobsTableBody">
                            {updateHistoryDetails.map((data, index) => (
                              <tr
                                key={data?.DeviceUUID}
                                className={`row-${index + 1}`}
                              >
                                <td className="version">
                                  <span className="text-h2">
                                    {extractVersion(data?.releaseVersion)}
                                  </span>
                                </td>

                                <td className="type">
                                  <span className="text-h2">
                                    {data?.releaseType}
                                  </span>
                                </td>

                                <td className="deviceSchedulePushedOn">
                                  <span className="text-h2">
                                    {formatDate(data?.updatedOn, false)}
                                  </span>
                                </td>
                                <td className="deviceSchedulePushedByUserEmail">
                                  <span className="text-h2">
                                    {data?.initiateUserEmail}
                                  </span>
                                </td>
                                <td className="infoIcon">
                                  <span
                                    onClick={() => {
                                      setIsUpdateModalDesc(true);
                                      setOnClickedIndex(index + 1);
                                    }}
                                  >
                                    {Icons.infoIcon}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        ) : (
                          <tbody>
                            <tr>
                              <td colspan="4">
                                <div id="noAssoDevice">No data Available</div>
                              </td>
                            </tr>
                          </tbody>
                        )}
                      </table>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            )}
            {/* Accordion Section Ends */}

            <div className="btnContainer">
              <button
                id="closeBtn"
                onClick={closeModal}
                className="roundBtn primary heading-h3"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Err/Success Modal */}
        <ModalComponent
          idName="schedule-info-modal"
          isOpen={infoModal.open}
          onCloseModal={onInfoModalClose}
          descriptionText={t(infoModal.msg)}
        />
      </Modal>

      <ModalComponent
        idName="install-confirm-modal"
        isOpen={confirmModal}
        onCloseModal={() => setConfirmModal(false)}
        needConfirmation={true}
        onConfirm={initiateOTA}
        primaryBtnText="Install"
        secondaryBtnText="Cancel"
      >
        <span className="heading heading-h3">
          {t("installInstruction.heading")}
        </span>

        <ul>
          <li>
            <span className="heading-h5">
              1. {t("installInstruction.row1")}
            </span>
          </li>
          <li>
            <span className="heading-h5">
              2. {t("installInstruction.row2")}
            </span>
          </li>
          <li>
            <span className="heading-h5">
              3. {t("installInstruction.row3")}
            </span>
          </li>
          <li>
            <span className="heading-h5">
              4. {t("installInstruction.row4")}
            </span>
          </li>
        </ul>

        <span className="installDiv">
          <span className="heading-h5">{t("installInstruction.row5")}</span>
          <span className="installText heading-h3">
            {t("installInstruction.row6")}
          </span>
        </span>
      </ModalComponent>

      {/* Updates History Info Modal starts */}
      <ModalComponent
        idName="release-version-modal"
        isOpen={isUpdateModalDesc}
        onCloseModal={() => setIsUpdateModalDesc(false)}
        okBtnText="Close"
      >
        {onClickedIndex && (
          <div>
            <div className="releaseVersionContainer">
              <span className="releaseVersion heading-h1">
                {extractVersion(
                  updateHistoryDetails[onClickedIndex - 1].releaseVersion
                ) || "No Update Available"}
              </span>
              &nbsp;&nbsp;&nbsp;
              <span className="releaseType text-h3">
                {updateHistoryDetails[onClickedIndex - 1]?.releaseType
                  ? `(${updateHistoryDetails[onClickedIndex - 1].releaseType})`
                  : ""}
              </span>
            </div>

            <div className="releaseDetailsContainer">
              {updateHistoryDetails && (
                <>
                  <div className="notesDesc text-h1">
                    {updateHistoryDetails[onClickedIndex - 1].releaseNote[0]}
                  </div>

                  <ul>
                    {updateHistoryDetails[onClickedIndex - 1].releaseNote
                      .slice(1)
                      .map((note, index) => (
                        <li key={index}>
                          <span className="text-h1">{note}</span>
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        )}
      </ModalComponent>
      {/* Updates History Info Modal closed */}
    </div>
  );
}

export default OTAUpdates;
